import { createSelector } from 'reselect'
import assert from 'assertf'

import { getDataTypeSchemasRaw, getDataTypeSchemas } from './schema'
import { getArgN, makeAPI } from './utils'

import * as selfExports from './datatype'

export { getDataTypeSchemas as getDataTypes }

/*
 * Datatype parsing.
 * Somewhat equivalent to datatype sections of FormatExt.c
 */

const getBaseToDatatype = createSelector([ getDataTypeSchemasRaw ], (schemas) => {
  const baseToDatatype = {}
  for (let datatypeName in schemas) {
    const datatype = schemas[datatypeName]
    assert.ok(
      !(datatype.input in baseToDatatype),
      'should not have duplicate bases. %s: %s',
      datatypeName, baseToDatatype[datatype.input]
    )
    baseToDatatype[datatype.input] = datatypeName
  }
  return baseToDatatype
})

const getDatatypeToBase = createSelector([ getDataTypeSchemasRaw ], (schemas) => {
  const datatypeToBase = {}
  for (let datatypeName in schemas) {
    const datatype = schemas[datatypeName]
    assert.ok(
      !(datatypeName in datatypeToBase),
      'should not have duplicate datatype names. %s: %s',
      datatypeName, datatypeToBase[datatypeName]
    )
    datatypeToBase[datatypeName] = datatype.input
  }
  return datatypeToBase
})

/**
 * Get names of all known bases.
 *
 * @returns {Array<String>}
 */

export const getBases = createSelector([ getBaseToDatatype ], (baseToDatatype) => {
  return Object.keys(baseToDatatype)
})

/**
 * Get names of all known DataTypes.
 *
 * @returns {Array<String>}
 */

export const all = createSelector([ getDatatypeToBase ], (datatypeToBase) => {
  return Object.keys(datatypeToBase)
})

/**
 * Match a known base against `baseName`.
 * Only partial match from start of `baseName` is required, longest matching
 * base will be selected. i.e. `baseName` can have trailing characters.
 *
 * @throws {AssertionError} If cannot match base.
 * @see DataType.hasBase
 *
 * @param {String} baseName
 * @returns {String} Found base
 */

export const getBase = createSelector([ getBases, getArgN(1) ], (bases, baseName) => {
  // Deal with tomo vs tomo_float
  // i.e. return longest matching start
  const base = bases
    .filter(base => baseName.indexOf(base) === 0)
    .sort((a, b) => a.length - b.length)
    .pop()

  return base
})

/**
 * Check if can match a known base against `baseName`.
 *
 * @param {String} baseName
 * @returns {Boolean}
 */

export const hasBase = createSelector([ getBase ], (base) => {
  return !!base
})

/**
 * Get meta information about a datatype.
 * i.e. object with input and output details.
 *
 * @throws {AssertionError} If datatype is invalid.
 * @see DataType.hasDatatype
 *
 * @param {String} datatype
 * @returns {Object}
 */

export const getDatatype = createSelector([ getDataTypeSchemasRaw, getArgN(1) ], (datatypes, datatype) => {
  const datatypeInfo = datatypes[datatype]
  assert(datatypeInfo, '%s invalid datatype', datatype)
  return datatypeInfo
})

/**
 * Ensure datatype is valid
 *
 * @param {String} datatype
 * @returns {Boolean}
 */

export const hasDatatype = createSelector([ getDataTypeSchemasRaw, getArgN(1) ], (datatypes, datatype) => {
  return !!datatypes[datatype]
})

/**
 * Get base from datatype.
 *
 * @throws {AssertionError} If datatype is invalid.
 * @see DataType.hasDatatype
 *
 * @param {String} datatype
 * @returns {Array<String>}
 */

export const getBaseFromDatatype = createSelector([ getDatatypeToBase, getArgN(1) ], (datatypeToBase, datatype) => {
  const base = datatypeToBase[datatype]
  assert.ok(base, 'Datatype %s requires a valid base.', datatype)
  return base
})

/**
 * Get datatype from base.
 *
 * @throws {AssertionError} If base is invalid.
 * @see DataType.hasBase
 *
 * @param {String} base
 * @returns {Array<String>}
 */

export const getDatatypeFromBase = createSelector([ getBaseToDatatype, getArgN(1) ], (baseToDatatype, base) => {
  const datatype = baseToDatatype[base]
  assert.ok(datatype, 'Base %s requires a valid datatype.', base)
  return datatype
})

export const getAPI = makeAPI(selfExports, [ getDataTypeSchemasRaw ])



// WEBPACK FOOTER //
// ../webmango-core/selectors/datatype.js